/**
 * Board.tsx — the 7-realm playing field laid out left-to-right. Active
 * realm gets a pulse glow; drop-targets gate per-realm.
 */

import type { CardData, PlayerId, RealmZone } from '../types.js';
import { RealmTile } from './RealmTile.js';

type Props = {
  realms: readonly RealmZone[];
  viewer: PlayerId;
  opponent: PlayerId;
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  /** Index into realms[] for the currently-active realm; null when none. */
  activeRealmIdx?: number | null | undefined;
  /** Current round (1..7). Realm i opens when round >= i+1. Realms
   *  beyond `currentRound` are rendered closed (locked, no drop). */
  currentRound?: number;
  onPlacementContextMenu?: ((cardId: string, e: React.MouseEvent) => void) | undefined;
  onSeizeStone?: ((realmIdx: number, player: PlayerId | null) => void) | undefined;
};

export function Board({
  realms,
  viewer,
  opponent,
  cards,
  imageUrl,
  activeRealmIdx,
  currentRound = 1,
  onPlacementContextMenu,
  onSeizeStone,
}: Props) {
  // 7 vertical lanes side by side, each filling the full container
  // height so the play area extends as long as the available space.
  // Realms are identified by their gemstone PNG (no text labels).
  return (
    <div className="grid h-full grid-cols-7 gap-2 px-2 py-2">
      {realms.map((realm, idx) => (
        <RealmTile
          key={realm.name}
          realm={realm.name}
          placements={realm.placements}
          stone={realm.stone}
          viewer={viewer}
          opponent={opponent}
          cards={cards}
          imageUrl={imageUrl}
          active={activeRealmIdx === idx}
          isOpen={currentRound >= idx + 1}
          onPlacementContextMenu={onPlacementContextMenu}
          onSeizeStone={(p) => onSeizeStone?.(idx, p)}
        />
      ))}
    </div>
  );
}
